/* GENERATED FILE — DO NOT EDIT.
 * Source: design/tokens.json
 * Regenerate: npm run tokens:build
 * Editing this file is a defect under DECISIONS.md D-025 boundary 2.
 */

/**
 * Identity, with a mutable return type.
 *
 * The artifact is emitted `as const` so no consumer can mutate a token — but
 * `as const` also turns every array into a `readonly` tuple, and React
 * Native's `TextStyle['fontVariant']` is a MUTABLE array type. Without this,
 * a correct native consumer cannot assign a correct generated role, and the
 * harness has to widen the type locally — which is a fork of the artifact
 * wearing a cast. Values are unchanged; only the emitted TYPE differs.
 */
function mutable<T extends string | number>(...values: T[]): T[] {
  return values;
}

export const fontFamily = {
  serif: 'Source Serif 4',
  sans: 'Inter',
} as const;

export const color = {
  ink: {
    primary: '#1D1D1D',
    secondary: '#4D4D4D',
    tertiary: '#6A6A6A',
    onInverse: '#FFFFFF',
  },
  surface: {
    page: '#FFFFFF',
    recessed: '#F4F4F4',
    inverse: '#1D1D1D',
    groundPhoto: '#EFEFEF',
    fabricSurround: {
      book: '#F7F7F7',
      assessment: '#949494',
      room: '#3D3D3D',
    },
  },
  line: {
    hairline: '#D3D3D3',
    rule: '#858585',
    strong: '#1D1D1D',
  },
  focus: {
    inner: '#1D1D1D',
    outer: '#FFFFFF',
  },
  alert: {
    ink: '#8C2F22',
  },
  thread: {
    base: '#1D1D1D',
    onDark: '#FFFFFF',
  },
} as const;

export const state = {
  hover: {
    surface: '#F4F4F4',
    inkFill: '#333333',
  },
  pressed: {
    inkFill: '#000000',
  },
  pending: {
    inkFill: '#565656',
  },
  selected: {
    markSize: 10,
    markColor: '#1D1D1D',
    labelWeight: '600',
  },
} as const;

export const space = {
  s100: 4,
  s200: 8,
  s300: 12,
  s400: 16,
  s600: 24,
  s800: 32,
  s1200: 48,
  s1600: 64,
  s2400: 96,
  s3200: 128,
  field: 8,
  row: 12,
  block: 24,
  section: 48,
  chapter: 96,
  gutter: 24,
} as const;

/** Density override MAP, not a second scale. Three aliases differ; nothing else does. */
export const spaceBackOffice = {
  s100: 4,
  s200: 8,
  s300: 12,
  s400: 16,
  s600: 24,
  s800: 32,
  s1200: 48,
  s1600: 64,
  s2400: 96,
  s3200: 128,
  field: 8,
  row: 12,
  block: 16,
  section: 32,
  chapter: 48,
  gutter: 24,
} as const;

export const layout = {
  breakpoint: {
    compact: 0,
    medium: 600,
    regular: 1024,
    wide: 1440,
  },
  columns: {
    compact: 4,
    medium: 8,
    regular: 12,
    wide: 12,
  },
  margin: {
    compact: 24,
    medium: 32,
    regular: 48,
    wide: 64,
  },
  maxWidth: {
    page: 1280,
    ledger: 720,
    controls: 400,
    login: 360,
    macroSwatch: 560,
  },
  measure: {
    proseNative: 560,
  },
  target: {
    min: 44,
  },
  swatchTile: {
    columns: {
      compact: 2,
      medium: 3,
      regular: 4,
      wide: 4,
    },
    aspect: {
      compact: 1,
      medium: 1.25,
      regular: 1.25,
      wide: 1.25,
    },
  },
  fabricHero: {
    minAreaRatio: 0.6,
    viewportMinAreaRatio: 0.6,
    viewportCompactHeight: 55,
  },
  portrait: {
    width: 140,
    thumbWidth: 96,
    aspect: 1.3333,
  },
  surroundBand: {
    cloth: 24,
    macro: 48,
  },
} as const;

export const radius = {
  none: 0,
  control: 10,
} as const;

export const borderWidth = {
  hairline: 1,
  rule: 1,
  emphasis: 2,
} as const;

export const focus = {
  width: {
    inner: 2,
    outer: 2,
  },
  offset: 2,
  radius: 12,
} as const;

export const icon = {
  set: 'chevron,arrow,close',
  size: 20,
  strokeWidth: 1.5,
} as const;

export const control = {
  button: {
    height: 48,
    paddingX: 24,
    radius: 10,
    primary: {
      fill: '#1D1D1D',
      fillHover: '#333333',
      fillPressed: '#000000',
      fillPending: '#565656',
      label: '#FFFFFF',
    },
    secondary: {
      borderColor: '#858585',
      borderWidth: 1,
      label: '#1D1D1D',
    },
  },
  input: {
    height: 48,
    paddingX: 12,
    radius: 10,
    borderColor: '#858585',
    borderColorFocus: '#1D1D1D',
    borderWidth: 1,
    background: '#FFFFFF',
    placeholderColor: '#6A6A6A',
    labelGap: 8,
    errorRuleWidth: 2,
    errorRuleColor: '#8C2F22',
  },
  radio: {
    size: 20,
    color: '#1D1D1D',
    gap: 16,
  },
  row: {
    ledgerHeight: 44,
    tableHeight: 40,
  },
  login: {
    opticalTopRatio: 0.38,
  },
} as const;

/** Milliseconds. `instant` is a real exported zero and must survive tree-shaking. */
export const duration = {
  instant: 0,
  acknowledge: 90,
  settle: 160,
  enter: 240,
  exit: 140,
} as const;

/** Every duration at zero. MOTION_SPEC §7.1 — one rule, no per-case exemptions. */
export const durationReduced = {
  instant: 0,
  acknowledge: 0,
  settle: 0,
  enter: 0,
  exit: 0,
} as const;

/** Four-number arrays, never strings. Apply as Easing.bezier(...easing.settle). */
export const easing = {
  settle: [0.35, 0, 0.15, 1],
  exit: [0.4, 0, 1, 1],
  linear: [0, 0, 1, 1],
} as const;

export const threshold = {
  silent: 120,
  skeletonMin: 200,
  priceHold: 600,
  slow: 5000,
} as const;

export const z = {
  base: 0,
  raised: 1,
  chrome: 10,
  focusRing: 20,
  systemSheet: 100,
} as const;

/** RN TextStyle-compatible. Figures are composed into EVERY role because RN does not inherit them. */
export const typeRole = {
  wordmark: { fontFamily: 'Source Serif 4', fontSize: 20, fontWeight: '400', lineHeight: 28, letterSpacing: 2, color: '#1D1D1D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  pageTitle: { fontFamily: 'Inter', fontSize: 40, fontWeight: '600', lineHeight: 44, letterSpacing: -1.2, color: '#1D1D1D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  sectionTitle: { fontFamily: 'Inter', fontSize: 28, fontWeight: '600', lineHeight: 35, letterSpacing: -0.56, color: '#1D1D1D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  blockTitle: { fontFamily: 'Inter', fontSize: 20, fontWeight: '600', lineHeight: 28, letterSpacing: -0.4, color: '#1D1D1D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  body: { fontFamily: 'Inter', fontSize: 16, fontWeight: '400', lineHeight: 26, letterSpacing: 0, color: '#1D1D1D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  bodyStrong: { fontFamily: 'Inter', fontSize: 16, fontWeight: '500', lineHeight: 26, letterSpacing: 0, color: '#1D1D1D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  supporting: { fontFamily: 'Inter', fontSize: 16, fontWeight: '400', lineHeight: 26, letterSpacing: 0, color: '#4D4D4D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  fact: { fontFamily: 'Inter', fontSize: 14, fontWeight: '400', lineHeight: 21, letterSpacing: 0, color: '#4D4D4D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  factEmphasis: { fontFamily: 'Inter', fontSize: 14, fontWeight: '500', lineHeight: 21, letterSpacing: 0, color: '#1D1D1D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  label: { fontFamily: 'Inter', fontSize: 14, fontWeight: '400', lineHeight: 21, letterSpacing: 0, color: '#4D4D4D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  recordValue: { fontFamily: 'Inter', fontSize: 20, fontWeight: '500', lineHeight: 28, letterSpacing: -0.4, color: '#1D1D1D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  eyebrow: { fontFamily: 'Inter', fontSize: 12, fontWeight: '600', lineHeight: 17, letterSpacing: 1.2, color: '#4D4D4D', textTransform: 'uppercase', fontVariant: mutable('tabular-nums', 'lining-nums') },
  fine: { fontFamily: 'Inter', fontSize: 12, fontWeight: '400', lineHeight: 17, letterSpacing: 0, color: '#6A6A6A', fontVariant: mutable('tabular-nums', 'lining-nums') },
  priceTotal: { fontFamily: 'Inter', fontSize: 28, fontWeight: '600', lineHeight: 35, letterSpacing: -0.56, color: '#1D1D1D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  priceLine: { fontFamily: 'Inter', fontSize: 16, fontWeight: '400', lineHeight: 26, letterSpacing: 0, color: '#1D1D1D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  tableCell: { fontFamily: 'Inter', fontSize: 14, fontWeight: '400', lineHeight: 21, letterSpacing: 0, color: '#1D1D1D', fontVariant: mutable('tabular-nums', 'lining-nums') },
  action: { fontFamily: 'Inter', fontSize: 16, fontWeight: '600', lineHeight: 26, letterSpacing: 0, fontVariant: mutable('tabular-nums', 'lining-nums') },
} as const;

/** Only the roles whose size or line height differs below layout.breakpoint.medium. */
export const typeRoleCompact = {
  pageTitle: { fontSize: 32, lineHeight: 35, letterSpacing: -0.96 },
  sectionTitle: { fontSize: 24, lineHeight: 31, letterSpacing: -0.48 },
  priceTotal: { fontSize: 24, lineHeight: 31, letterSpacing: -0.48 },
} as const;

export type Breakpoint = 'compact' | 'medium' | 'regular' | 'wide';

/** The only sanctioned way to read a role. The native harness calls typography('compact'). */
export function typography(bp: Breakpoint) {
  if (bp !== 'compact') return typeRole;
  return {
    ...typeRole,
    pageTitle: { ...typeRole.pageTitle, ...typeRoleCompact.pageTitle },
    sectionTitle: { ...typeRole.sectionTitle, ...typeRoleCompact.sectionTitle },
    priceTotal: { ...typeRole.priceTotal, ...typeRoleCompact.priceTotal },
  };
}

/** type.role.link is a modifier — it carries no family, size or line height. */
export const typeModifier = {
  link: { fontWeight: '400', tracking: 0, color: '#1D1D1D', underlineColor: '#858585', underlineColorHover: '#1D1D1D', underlineThickness: 1, underlineOffset: 3 },
} as const;

/** Every native-eligible token, addressed by its source path. Same numbers, one generation. */
export const tokens = {
  font: {
    family: {
      serif: {
        name: 'Source Serif 4',
        stack: ['Source Serif 4', 'Charter', 'Iowan Old Style', 'Georgia', 'Times New Roman', 'serif'],
        weights: '400',
      },
      sans: {
        name: 'Inter',
        stack: ['Inter', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'],
        weights: '400,500,600',
      },
    },
  },
  type: {
    size: {
      '100': 12,
      '200': 14,
      '300': 16,
      '400': 20,
      '500': 28,
      '600': 40,
    },
    lineHeight: {
      '100': 17,
      '200': 21,
      '300': 26,
      '400': 28,
      '500': 35,
      '600': 44,
    },
    weight: {
      regular: '400',
      medium: '500',
      semibold: '600',
    },
    tracking: {
      display: -0.03,
      tight: -0.02,
      normal: 0,
      wide: 0.02,
      caps: 0.1,
    },
    figures: {
      default: mutable('tabular-nums', 'lining-nums'),
    },
    underline: {
      color: '#858585',
      colorHover: '#1D1D1D',
      thickness: 1,
      offset: 3,
    },
  },
  color: {
    scheme: 'light',
    ink: {
      primary: '#1D1D1D',
      secondary: '#4D4D4D',
      tertiary: '#6A6A6A',
      onInverse: '#FFFFFF',
    },
    surface: {
      page: '#FFFFFF',
      recessed: '#F4F4F4',
      inverse: '#1D1D1D',
      groundPhoto: '#EFEFEF',
      fabricSurround: {
        book: '#F7F7F7',
        assessment: '#949494',
        room: '#3D3D3D',
      },
    },
    line: {
      hairline: '#D3D3D3',
      rule: '#858585',
      strong: '#1D1D1D',
    },
    focus: {
      inner: '#1D1D1D',
      outer: '#FFFFFF',
    },
    alert: {
      ink: '#8C2F22',
    },
    thread: {
      base: '#1D1D1D',
      onDark: '#FFFFFF',
    },
  },
  state: {
    hover: {
      surface: '#F4F4F4',
      inkFill: '#333333',
    },
    pressed: {
      inkFill: '#000000',
    },
    pending: {
      inkFill: '#565656',
    },
    selected: {
      markSize: 10,
      markColor: '#1D1D1D',
      labelWeight: '600',
    },
  },
  space: {
    '100': 4,
    '200': 8,
    '300': 12,
    '400': 16,
    '600': 24,
    '800': 32,
    '1200': 48,
    '1600': 64,
    '2400': 96,
    '3200': 128,
    field: 8,
    row: 12,
    block: 24,
    section: 48,
    chapter: 96,
    gutter: 24,
    density: {
      backOffice: {
        block: 16,
        section: 32,
        chapter: 48,
      },
    },
  },
  layout: {
    breakpoint: {
      compact: 0,
      medium: 600,
      regular: 1024,
      wide: 1440,
    },
    columns: {
      compact: 4,
      medium: 8,
      regular: 12,
      wide: 12,
    },
    margin: {
      compact: 24,
      medium: 32,
      regular: 48,
      wide: 64,
    },
    maxWidth: {
      page: 1280,
      ledger: 720,
      controls: 400,
      login: 360,
      macroSwatch: 560,
    },
    measure: {
      proseNative: 560,
    },
    target: {
      min: 44,
    },
    swatchTile: {
      columns: {
        compact: 2,
        medium: 3,
        regular: 4,
        wide: 4,
      },
      aspect: {
        compact: 1,
        medium: 1.25,
        regular: 1.25,
        wide: 1.25,
      },
    },
    fabricHero: {
      minAreaRatio: 0.6,
      viewportMinAreaRatio: 0.6,
      viewportCompactHeight: 55,
    },
    portrait: {
      width: 140,
      thumbWidth: 96,
      aspect: 1.3333,
    },
    surroundBand: {
      cloth: 24,
      macro: 48,
    },
  },
  radius: {
    none: 0,
    control: 10,
  },
  borderWidth: {
    hairline: 1,
    rule: 1,
    emphasis: 2,
  },
  focus: {
    width: {
      inner: 2,
      outer: 2,
    },
    offset: 2,
    radius: 12,
  },
  icon: {
    set: 'chevron,arrow,close',
    size: 20,
    strokeWidth: 1.5,
  },
  control: {
    button: {
      height: 48,
      paddingX: 24,
      radius: 10,
      primary: {
        fill: '#1D1D1D',
        fillHover: '#333333',
        fillPressed: '#000000',
        fillPending: '#565656',
        label: '#FFFFFF',
      },
      secondary: {
        borderColor: '#858585',
        borderWidth: 1,
        label: '#1D1D1D',
      },
    },
    input: {
      height: 48,
      paddingX: 12,
      radius: 10,
      borderColor: '#858585',
      borderColorFocus: '#1D1D1D',
      borderWidth: 1,
      background: '#FFFFFF',
      placeholderColor: '#6A6A6A',
      labelGap: 8,
      errorRuleWidth: 2,
      errorRuleColor: '#8C2F22',
    },
    radio: {
      size: 20,
      color: '#1D1D1D',
      gap: 16,
    },
    row: {
      ledgerHeight: 44,
      tableHeight: 40,
    },
    login: {
      opticalTopRatio: 0.38,
    },
  },
  motion: {
    duration: {
      instant: 0,
      acknowledge: 90,
      settle: 160,
      enter: 240,
      exit: 140,
    },
    easing: {
      settle: [0.35, 0, 0.15, 1],
      exit: [0.4, 0, 1, 1],
      linear: [0, 0, 1, 1],
    },
    threshold: {
      silent: 120,
      skeletonMin: 200,
      priceHold: 600,
      slow: 5000,
    },
  },
  z: {
    base: 0,
    raised: 1,
    chrome: 10,
    focusRing: 20,
    systemSheet: 100,
  },
} as const;

/** WEB-ONLY. $platforms: ["web"] tokens that TypeScript consumers need
 *  (garment physics, prose measure, print sizes). Importing these in
 *  apps/native is a defect — they are not part of the native contract. */
export const web = {
  font: {
    synthesis: 'none',
    display: 'optional',
    preload: 'sans.400,sans.600,serif.400',
  },
  type: {
    figures: {
      identifier: mutable('tabular-nums', 'lining-nums', 'slashed-zero'),
    },
  },
  color: {
    print: {
      paper: '#FFFFFF',
      ink: '#000000',
    },
  },
  layout: {
    measure: {
      prose: 58,
    },
  },
  motion: {
    garment: {
      rotationGainDegPerPx: 0.3,
      rotationGainClampDegMin: 120,
      rotationGainClampDegMax: 240,
      inertiaDamping: 0.92,
      inertiaDampingFrameMs: 16.67,
      inertiaMaxVelocityDegPerSec: 220,
      inertiaStopThresholdDegPerSec: 8,
      inertiaSampleWindowMs: 60,
      zoomMin: 1,
      zoomMax: 2,
      zoomWheelStep: 0.12,
      axisLockThresholdPx: 12,
      axisLockDominanceRatio: 2,
      pitchLockedRound1: true,
    },
  },
  print: {
    ruleWidth: 0.5,
    size: {
      body: 10,
      table: 9,
      value: 11,
    },
  },
} as const;
